import type { NativeAuthProvider, NativeWelcomeSectionId } from './analytics'

export type NativeAuthIntentMode = 'signup' | 'signin'

export type NativeAuthIntent = {
  mode: NativeAuthIntentMode
  provider: NativeAuthProvider
  platform: string
  entrySection: NativeWelcomeSectionId
  startedAt: number
}

const NATIVE_AUTH_INTENT_KEY = 'optioneer_native_auth_intent'
const NATIVE_AUTH_INTENT_MAX_AGE_MS = 2 * 60 * 1000

function canUseSessionStorage() {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined'
}

function isNativeWelcomeSectionId(value: unknown): value is NativeWelcomeSectionId {
  return value === 'hero' || value === 'portfolio' || value === 'wheel' || value === 'discovery'
}

function normalizeLegacyEntrySection(value: unknown): NativeWelcomeSectionId | null {
  if (isNativeWelcomeSectionId(value)) return value
  if (value === 'screener') return 'discovery'
  return null
}

export function storeNativeAuthIntent(intent: Omit<NativeAuthIntent, 'startedAt'>) {
  if (!canUseSessionStorage()) return

  window.sessionStorage.setItem(
    NATIVE_AUTH_INTENT_KEY,
    JSON.stringify({ ...intent, startedAt: Date.now() })
  )
}

export function readNativeAuthIntent(): NativeAuthIntent | null {
  if (!canUseSessionStorage()) return null

  const raw = window.sessionStorage.getItem(NATIVE_AUTH_INTENT_KEY)
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as Partial<NativeAuthIntent>
    if (parsed.mode !== 'signup' && parsed.mode !== 'signin') {
      return null
    }
    if (
      parsed.provider !== 'email' &&
      parsed.provider !== 'google' &&
      parsed.provider !== 'apple'
    ) {
      return null
    }
    const entrySection =
      normalizeLegacyEntrySection(parsed.entrySection) ??
      normalizeLegacyEntrySection(
        (parsed as Partial<NativeAuthIntent> & { selectedFeature?: unknown }).selectedFeature
      )
    if (!parsed.platform || !entrySection || typeof parsed.startedAt !== 'number') {
      return null
    }

    return { ...(parsed as NativeAuthIntent), entrySection }
  } catch {
    return null
  }
}

export function clearNativeAuthIntent() {
  if (!canUseSessionStorage()) return
  window.sessionStorage.removeItem(NATIVE_AUTH_INTENT_KEY)
}

export function hasActiveNativeAuthIntent() {
  const intent = readNativeAuthIntent()
  if (!intent) return false
  return Date.now() - intent.startedAt <= NATIVE_AUTH_INTENT_MAX_AGE_MS
}

export function consumeNativeAuthIntent() {
  const intent = readNativeAuthIntent()
  clearNativeAuthIntent()
  return intent
}
